import * as FileSystem from 'expo-file-system/legacy';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';
import { Platform, Image } from 'react-native';
import uuid from 'react-native-uuid';

import { LocalFile } from '@graphql/generated';
import { fetchWithErrorHandling } from '@utils/fetchWithErrorHandling';

const MAX_DIMENSION = 2048;
const TARGET_MIN_SIZE = 400 * 1024;
const TARGET_MAX_SIZE = 700 * 1024;
const MIN_QUALITY = 0.3;
const INITIAL_QUALITY = 0.9;
const TARGET_CENTER_SIZE = (TARGET_MIN_SIZE + TARGET_MAX_SIZE) / 2;
const MAX_COMPRESSION_ATTEMPTS = 3;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const estimateInitialQuality = (originalSize: number): number => {
  if (!originalSize || originalSize <= TARGET_MAX_SIZE) {
    return INITIAL_QUALITY;
  }

  const ratio = TARGET_CENTER_SIZE / originalSize;
  const estimated = INITIAL_QUALITY * ratio;

  return clamp(estimated, MIN_QUALITY, INITIAL_QUALITY);
};

const adjustQuality = (quality: number, lastSize: number): number => {
  if (!lastSize) return quality;

  // Still too big -> reduce quality more aggressively
  if (lastSize > TARGET_MAX_SIZE) {
    const ratio = TARGET_MAX_SIZE / lastSize;
    const next = quality * ratio * 0.85;
    return clamp(next, MIN_QUALITY, INITIAL_QUALITY);
  }

  // Too small (far below MIN size) -> we can slightly increase quality
  if (lastSize < TARGET_MIN_SIZE) {
    const ratio = TARGET_MIN_SIZE / lastSize;
    const next = quality * ratio;
    return clamp(next, MIN_QUALITY, INITIAL_QUALITY);
  }

  return quality;
};

// Helper: get image dimensions on native
const getImageDimensionsNative = async (
  uri: string
): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    Image.getSize(
      uri,
      (width, height) => {
        resolve({ width, height });
      },
      (error) => {
        console.warn('Failed to get image size:', error);
        resolve(null);
      }
    );
  });
};

type CompressionResult = {
  uri: string;
  width: number;
  height: number;
  base64?: string;
};

// expo-image-manipulator is imported statically: a dynamic import() is served
// as a lazy bundle in dev and fails with LoadBundleFromServerRequestError,
// silently skipping compression (TA-11049).
const compressImageNative = async (
  uri: string,
  quality: number,
  resizeAction?: { resize: { width?: number; height?: number } }
): Promise<CompressionResult> => {
  // 1. Create a manipulation context
  let ctx = ImageManipulator.manipulate(uri);

  // 2. Apply resize if provided
  if (resizeAction) {
    ctx = ctx.resize(resizeAction.resize);
  }

  // 3. Render the modified image into an ImageRef
  const imageRef = await ctx.renderAsync();

  // 4. Save the image with compression + format
  const saved = await imageRef.saveAsync({
    compress: quality,
    format: SaveFormat.JPEG,
  });

  return {
    uri: saved.uri,
    width: saved.width,
    height: saved.height,
    base64: saved.base64,
  };
};

/**
 * Native camera JPEGs (expo-camera with skipProcessing) keep the sensor's
 * pixel orientation plus an EXIF Orientation tag. iOS's Image ignores the tag,
 * and on Android Image.getSize and the markup screen do too, so the photo
 * shows rotated or squashed. ImageManipulator applies the EXIF orientation
 * when it loads an image, so re-saving bakes it into the pixels. Returns the
 * original uri on web or on failure (TA-11049).
 */
export const bakeImageOrientation = async (uri: string): Promise<string> => {
  if (Platform.OS === 'web') return uri;
  try {
    const imageRef = await ImageManipulator.manipulate(uri).renderAsync();
    const saved = await imageRef.saveAsync({
      compress: 1,
      format: SaveFormat.JPEG,
    });
    return saved.uri;
  } catch (error) {
    console.error('Failed to bake image orientation:', error);
    return uri;
  }
};

const getFileSizeNative = async (uri: string): Promise<number> => {
  const fileInfo = await FileSystem.getInfoAsync(uri, { size: true });
  return fileInfo.exists && 'size' in fileInfo ? fileInfo.size : 0;
};

const compressImageWeb = async (
  dataUrl: string,
  quality: number
): Promise<{ dataUrl: string; size: number }> => {
  const imageCompression = (await import('browser-image-compression')).default;

  const response = await fetchWithErrorHandling(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], 'image.jpg', {
    type: blob.type || 'image/jpeg',
  });

  const options = {
    maxSizeMB: TARGET_MAX_SIZE / (1024 * 1024),
    initialQuality: quality,
    useWebWorker: true,
    fileType: 'image/jpeg' as const,
  };

  const compressedFile = await imageCompression(file, options);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        dataUrl: reader.result as string,
        size: compressedFile.size,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(compressedFile);
  });
};

export const compressImage = async (
  localFile: LocalFile
): Promise<LocalFile> => {
  if (!localFile.isImage || localFile.isVideo || localFile.isAudio) {
    return localFile;
  }

  const currentSize = localFile.size ?? 0;

  // If image is already <= TARGET_MAX_SIZE (~700KB), skip compression.
  // We don't try to "enlarge" images to reach TARGET_MIN_SIZE.
  if (currentSize > 0 && currentSize <= TARGET_MAX_SIZE) {
    return localFile;
  }

  try {
    if (Platform.OS === 'web') {
      return await compressImageForWeb(localFile);
    } else {
      return await compressImageForNative(localFile);
    }
  } catch (error) {
    console.error('Image compression failed, returning original file:', error);
    return localFile;
  }
};

const compressImageForNative = async (
  localFile: LocalFile
): Promise<LocalFile> => {
  // Get original size, fallback to file system if missing
  let baseUri = localFile.url;
  let baseSize = localFile.size ?? (await getFileSizeNative(baseUri));

  // 1. Optional: downscale image if its dimensions are too large
  const dims = await getImageDimensionsNative(baseUri);

  if (dims) {
    const { width, height } = dims;
    const maxSide = Math.max(width, height);

    if (maxSide > MAX_DIMENSION) {
      // Compute resize options to keep aspect ratio
      let resize: { width?: number; height?: number } = {};

      if (width >= height) {
        // Landscape or square: limit width
        resize.width = MAX_DIMENSION;
      } else {
        // Portrait: limit height
        resize.height = MAX_DIMENSION;
      }

      // First pass: only resize, no extra compression (quality = 1)
      const resized = await compressImageNative(baseUri, 1, { resize });

      baseUri = resized.uri;
      baseSize = await getFileSizeNative(baseUri);
    }
  }

  // 2. Now run quality-based compression on the (possibly) downscaled image
  const originalSizeForQuality = baseSize;
  let quality = estimateInitialQuality(originalSizeForQuality);
  let compressedUri = baseUri;
  let compressedSize = baseSize;

  for (let attempt = 1; attempt <= MAX_COMPRESSION_ATTEMPTS; attempt++) {
    // No resize here; we already resized above if needed
    const result = await compressImageNative(baseUri, quality);
    const newSize = await getFileSizeNative(result.uri);

    // Track best candidate (smallest so far)
    if (newSize > 0 && newSize < compressedSize) {
      compressedUri = result.uri;
      compressedSize = newSize;
    }

    // If we are in the target range, stop early
    if (newSize >= TARGET_MIN_SIZE && newSize <= TARGET_MAX_SIZE) {
      compressedUri = result.uri;
      compressedSize = newSize;
      break;
    }

    if (attempt === MAX_COMPRESSION_ATTEMPTS) {
      break;
    }

    const nextQuality = adjustQuality(quality, newSize);

    // If adjustment is very small, further attempts won't help much
    if (Math.abs(nextQuality - quality) < 0.02) {
      quality = nextQuality;
      break;
    }

    quality = nextQuality;
  }

  // If we ended up not changing URI at all, just return the original file
  if (compressedUri === localFile.url) {
    return localFile;
  }

  const clientId = uuid.v4().toString();
  const fileName = `${clientId}.jpg`;
  const destUri = `${FileSystem.documentDirectory}${fileName}`;

  try {
    await FileSystem.copyAsync({ from: compressedUri, to: destUri });
  } catch (err) {
    console.error('Failed to copy compressed image:', err);
    return localFile;
  }

  return {
    ...localFile,
    id: clientId,
    clientId,
    name: fileName,
    url: destUri,
    size: compressedSize,
    contentType: 'image/jpeg',
  };
};

const compressImageForWeb = async (
  localFile: LocalFile
): Promise<LocalFile> => {
  const originalSize = localFile.size ?? 0;

  let quality = estimateInitialQuality(originalSize);
  let compressedDataUrl = localFile.url;
  let compressedSize = originalSize;

  for (let attempt = 1; attempt <= MAX_COMPRESSION_ATTEMPTS; attempt++) {
    const result = await compressImageWeb(localFile.url, quality);
    const newSize = result.size;

    if (newSize > 0 && newSize < compressedSize) {
      compressedDataUrl = result.dataUrl;
      compressedSize = newSize;
    }

    if (newSize >= TARGET_MIN_SIZE && newSize <= TARGET_MAX_SIZE) {
      compressedDataUrl = result.dataUrl;
      compressedSize = newSize;
      break;
    }

    if (attempt === MAX_COMPRESSION_ATTEMPTS || quality <= MIN_QUALITY) {
      break;
    }

    const nextQuality = adjustQuality(quality, newSize);

    if (Math.abs(nextQuality - quality) < 0.02) {
      quality = nextQuality;
      break;
    }

    quality = nextQuality;
  }

  if (compressedDataUrl === localFile.url) {
    return localFile;
  }

  const clientId = uuid.v4().toString();
  const fileName = `${clientId}.jpg`;

  return {
    ...localFile,
    id: clientId,
    clientId,
    name: fileName,
    url: compressedDataUrl,
    size: compressedSize,
    contentType: 'image/jpeg',
  };
};

export const compressImages = async (
  localFiles: LocalFile[]
): Promise<LocalFile[]> => {
  return Promise.all(
    localFiles.map((file) => (file.isImage ? compressImage(file) : file))
  );
};
